import React, { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

// Styling
import { defaultColor } from '../../styles/colors'
import { ThemeColor } from '../../common/models/colors'

// Components
import { Icon } from '../icon'

// Types
import { IFormControlProps } from '../../common/models/IFormControlProps'

export interface IProps extends Omit<IFormControlProps, 'value'> {
  color?: ThemeColor
  onClick?: () => void
  checked?: boolean
  className?: string
}

const Checkbox = (props: IProps) => {
  const color = props.color || defaultColor
  const themeContext = useContext(ThemeContext)
  // onClick is bound once on the wrapper; keep it off the input so a single click
  // doesn't fire twice (input target + wrapper bubble) and cancel its own toggle.
  const { onClick, ...inputProps } = props

  return (
    <CheckboxWrapper
      className={props.className}
      color={color}
      onClick={onClick}
    >
      <CheckboxInput type="checkbox" {...inputProps} />
      <CheckboxCheckmark>
        <Icon type="Check" size={10} color={themeContext.colors.lightest} />
      </CheckboxCheckmark>
      {props.label && <CheckboxLabel>{props.label}</CheckboxLabel>}
    </CheckboxWrapper>
  )
}

export const CheckboxInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: ${(props) => (props.disabled ? 'none' : 'block')};
  width: 100%;
  height: 100%;
  cursor: pointer;
  opacity: 0;
`

const CheckboxLabel = styled.label`
  display: flex;
  position: relative;
  left: 28px;
  line-height: 1;
  cursor: pointer;
`

export const CheckboxCheckmark = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 16px;
  height: 16px;
  transition: background-color 0.15s;
  border-radius: 4px;
`

export const CheckboxWrapper = styled.div<{ color: ThemeColor }>`
  position: relative;
  display: block;
  min-width: 16px;
  height: 16px;
  cursor: pointer;
  user-select: none;

  ${CheckboxCheckmark} {
    border: 2px solid ${({ theme, color }) => theme.colors[color]};

    > *:first-child {
      position: absolute;
      top: 1px;
      left: 1px;
      transition: opacity 0.15s;
    }
  }

  ${CheckboxLabel} {
    color: ${({ theme, color }) => theme.colors[color]};
  }

  ${CheckboxInput}:checked ~ ${CheckboxCheckmark} {
    background-color: ${({ theme, color }) => theme.colors[color]};
  }

  ${CheckboxInput}:not(:checked) ~ ${CheckboxCheckmark} > *:first-child {
    opacity: 0;
  }
`

export default Checkbox
